import { Component, OnDestroy, OnInit } from '@angular/core';
import { DataService } from '../../../services/data/data.service';
import { Subject, takeUntil } from 'rxjs';
import { RouterService } from '../../../services/router/router.service';
import { APP_ROUTES } from '../../../utils/route.constant';
import { IContractData } from '../../../interfaces/IJob';
import { SELECTED_CONTRACT_DATA } from '../../../utils/constants';
import { SPECIAL_MEMBER_OFFER_CONTENT } from './special-member-offer-v2.config';
import { LIFERAY_WEB_CONSTANT } from '../../../utils/liferay.constant';
import { ICrossSellContent, ICrossSellContentLiferayResponse } from '../../../interfaces/ICrossSellContent';
import { LiferayService } from '../../../services/liferay/liferay.service';
import { UtilitiesService } from '../../../services/utilities/utilities.service';

import { Router } from '@angular/router';
import { CALL_BUTTON_CROSS_SELL_CE_LANDING_PAGE, CE_START_ENROLL_FLOW } from '../../../services/ga4/ga4.constant';
import { Ga4Service } from '../../../services/ga4/ga4.service';
import { IMessage } from '../../../services/messages/messages.interface';
import { EnrollmentService } from '../../../services/enrollment/enrollment.service';
import { IBaseCoverageRequest, IBaseCoverageResponse } from '../../../interfaces/IEnrollment';
import { getMonthlyCost } from './special-member-offer-v2.helper';
declare const window: any;

@Component({
  selector: 'app-special-member-offer-v2',
  templateUrl: './special-member-offer-v2.component.html',
  styleUrl: './special-member-offer-v2.component.scss'
})
export class SpecialMemberOfferV2Component implements OnInit, OnDestroy {
  cleaner$ = new Subject<void>();
  contractData: IContractData | null = null;
  content = SPECIAL_MEMBER_OFFER_CONTENT;
  type: string | null = null;
  contentToBeDisplayed: ICrossSellContent | null = null;
  isSpecialMemberOfferFormUrl = false;
  errorMessage: IMessage | null = null;
  productDetails: IBaseCoverageResponse | null = null;
  sampleContractUrl: string | null = null;
  loading = false;

  get monthlyCost(): number | null {
    return getMonthlyCost(this.productDetails?.productInfo?.cost) || null;
  }

  get ctaDescription(): string | null {
    const cost = this.monthlyCost;
    if (!cost) {
      return null;
    }
    const formattedCost = cost.toFixed(2);
    return this.content.cta.description.replace('${monthlyCost}', formattedCost);
  }

  constructor(
    private data: DataService,
    private routerService: RouterService,
    private liferay: LiferayService,
    private utils: UtilitiesService,
    private router: Router,
    private gaService: Ga4Service,
    private enrollService: EnrollmentService,
  ) { }

  ngOnInit(): void {
    // Check if current URL is /special-member-offer-form-v2
    this.isSpecialMemberOfferFormUrl = this.router.url.startsWith('/special-member-offer-form-v2');
    this.type = this.routerService.getQueryParam('type');

    const stored = sessionStorage.getItem(SELECTED_CONTRACT_DATA);
    if (stored) {
      try {
        const parsed: IContractData = JSON.parse(stored);
        if (!parsed?.baseContract) {
          throw new Error('Invalid contract payload');
        }
        this.contractData = parsed;
        this.data.contractData$.next(this.contractData);
      } catch {
        sessionStorage.removeItem(SELECTED_CONTRACT_DATA);
        this.routerService.goto(APP_ROUTES.DASHBOARD);
        return;
      }
    }

    this.data.contractData$
      .pipe(takeUntil(this.cleaner$))
      .subscribe({
        next: (contract) => {
          if (contract === null) {
            this.routerService.goto(APP_ROUTES.DASHBOARD);
            return;
          };
          this.contractData = contract;
          this.getCrossSellContent();
          this.getCrossSellResources();
        }
      });

    this.data.messagesToDisplay$.pipe(takeUntil(this.cleaner$)).subscribe({
      next: (messages) => {
        if (messages && messages.length > 0) {
          this.errorMessage = messages.find(msg => msg.severity === 'error') || null;
        } else {
          this.errorMessage = null;
        }
      }
    });
  }

  getCrossSellContent(): void {
    const { type, id } = this.type === 'CE' ? LIFERAY_WEB_CONSTANT.cross_sell_ce_content : LIFERAY_WEB_CONSTANT.cross_sell_surge_content;
    this.liferay.getWebContent<ICrossSellContentLiferayResponse>(type, id)
      .pipe(takeUntil(this.cleaner$))
      .subscribe((webContent) => {
        if (!webContent) return;

        const contentToDisplayForClient = webContent.value?.content || [];
        const clientId = this.contractData?.baseContract?.clientId;
        const contentFound = contentToDisplayForClient.find(item => item.clientId === clientId) || null;
        this.contentToBeDisplayed = contentFound
          ? { ...contentFound, hoursOfOperation: this.utils.setHtmlContent(contentFound.hoursOfOperation || '') }
          : null;
        if (!this.isSpecialMemberOfferFormUrl) {
          this.getProductInfo();
          this.data.crossSellContent$.next(this.contentToBeDisplayed);
        }
      });
  }

  getCrossSellResources(): void {
    const { type, id } = LIFERAY_WEB_CONSTANT.cross_sell_resources;
    this.liferay.getWebContent<any>(type, id)
      .pipe(takeUntil(this.cleaner$))
      .subscribe((webContent) => {
        if (!webContent) return;
        const resources = webContent.value;
        if (Array.isArray(resources)) {
          const found = resources.find((item: any) => item.headingText === 'View annual sample contract');
          if (found && found.linkLabels && found.linkLabels.length > 0) {
            this.sampleContractUrl = found.linkLabels[0].linkUrl;
            this.data.sampleContractPdfUrl$.next(this.sampleContractUrl);
          }
        }
      });
  }

  openSampleContract(): void {
    if (this.sampleContractUrl) {
      window.open(this.sampleContractUrl, '_blank');
    }
  }
  scrollToForm(): void {
    const el = document.getElementById('special-member-offer-form');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }

  goToEnroll(): void {
    if (this.type === 'CE') {
      this.gaService.setGa4DtcSearsLanding(CE_START_ENROLL_FLOW, this.contractData?.baseContract?.clientId ?? 'Unknown Client');
    }
    // Track the enrollment start/success action in Optimizely
    try {
      if (window.optimizely) {
        window.optimizely.push({
          type: 'event',
          eventName: 'cross_sell_enroll_success'
        });
      }
    } catch (error) {
      console.warn('[Optimizely] Error tracking enroll click:', error);
    }

    this.type && this.routerService.navigateWithQuery(APP_ROUTES.SPECIAL_MEMBER_OFFER_V2_ENROLL_REVIEW, { type: this.type });
  }

  clearErrorMessage(): void {
    this.errorMessage = null;
    this.data.messagesToDisplay$.next([]);
  }

  getProductInfo(): void {
    if (!this.contractData?.baseContract?.contractNumber || !this.contractData?.baseContract?.propertyAddress?.zip || !this.contentToBeDisplayed?.productId) {
      return;
    }

    this.loading = true;
    const payload: IBaseCoverageRequest = {
      productId: this.contentToBeDisplayed.productId,
      zipCode: this.contractData?.baseContract?.propertyAddress?.zip,
    }

    this.enrollService.getBaseCoverage(payload)
      .pipe(takeUntil(this.cleaner$))
      .subscribe({
        next: (response) => {
          this.productDetails = response;
          this.loading = false;
          this.data.productInfo$.next(response);
        },
        error: () => {
          // need to check with PO on how to handle this
          this.loading = false;
        }
      })
  }

  ngOnDestroy(): void {
    this.cleaner$.next();
    this.cleaner$.complete();
    if (this.isSpecialMemberOfferFormUrl) {
      sessionStorage.removeItem(SELECTED_CONTRACT_DATA);
    }
  }
}
